import React, { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Input,
  Label,
  Badge,
} from 'lift-ui';
import { X, Trash2, CloudUpload, Paperclip, ChevronDown, Link, Plus, Globe, Loader2 } from 'lucide-react';
import { Toastify } from 'lift-ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export interface FileWithMetadata {
  file?: File;
  url?: string;
  title: string;
  description: string;
  isUrl: boolean;
}

interface UploadPantryDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (filesWithMetadata: FileWithMetadata[]) => Promise<void>;
}

// Favicon Component with error handling
const FaviconDisplay: React.FC<{ url: string; size?: number }> = ({ url, size = 20 }) => {
  const [faviconError, setFaviconError] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  React.useEffect(() => {
    setFaviconError(false);
    setFaviconUrl(null);

    if (!url || !url.trim()) {
      return;
    }

    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      // Using Google's favicon service
      const favicon = `https://www.google.com/s2/favicons?domain=${domain}&sz=${size * 2}`;
      setFaviconUrl(favicon);
    } catch (error) {
      setFaviconError(true);
    }
  }, [url, size]);

  if (!faviconUrl || faviconError) {
    return (
      <div
        className="rounded-full bg-blue-100 flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <Globe size={size * 0.6} className="text-blue-700" />
      </div>
    );
  }

  return (
    <img
      src={faviconUrl}
      alt="favicon"
      className="rounded-full border border-gray-200"
      style={{ width: size, height: size }}
      onError={() => setFaviconError(true)}
    />
  );
};

const UploadPantryDialog: React.FC<UploadPantryDialogProps> = ({
  open,
  onClose,
  onUpload,
}) => {
  const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithMetadata[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<{ [key: number]: { title?: string } }>({});
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [urlRows, setUrlRows] = useState<Array<{ url: string; title: string; description: string; id: number }>>([
    { url: '', title: '', description: '', id: 0 }
  ]);
  const [nextUrlId, setNextUrlId] = useState(1);
  const [urlValidationErrors, setUrlValidationErrors] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const processFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newFilesWithMetadata: FileWithMetadata[] = fileArray.map(file => ({
      file,
      title: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension for default title
      description: '',
      isUrl: false,
    }));
    setFilesWithMetadata((prev) => [...prev, ...newFilesWithMetadata]);
    // Auto-expand the first newly added file
    if (filesWithMetadata.length === 0 && newFilesWithMetadata.length > 0) {
      setExpandedIndex(0);
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'downloaded-file';
      return filename;
    } catch {
      return 'downloaded-file';
    }
  };

  const addUrlRow = () => {
    setUrlRows(prev => [...prev, { url: '', title: '', description: '', id: nextUrlId }]);
    setNextUrlId(prev => prev + 1);
  };

  const removeUrlRow = (id: number) => {
    setUrlRows(prev => prev.filter(row => row.id !== id));
  };

  const updateUrlRow = (id: number, field: 'url' | 'title' | 'description', value: string) => {
    setUrlRows(prev => prev.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
    // Clear validation errors when user starts typing
    if (urlValidationErrors.length > 0) {
      setUrlValidationErrors([]);
    }
  };

  const processUrlRowsToFiles = (): FileWithMetadata[] => {
    return urlRows
      .filter(row => row.url.trim() && row.title.trim()) // Only include rows with both URL and title
      .map(row => ({
        url: row.url.trim(),
        title: row.title.trim(),
        description: row.description.trim(),
        isUrl: true,
      }));
  };

  const hasValidUrlEntries = (): boolean => {
    return urlRows.some(row => row.url.trim() && row.title.trim());
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      processFiles(event.target.files);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFilesWithMetadata((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const handleMetadataChange = (index: number, field: 'title' | 'description', value: string) => {
    setFilesWithMetadata((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
    if (field === 'title') {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index].title;
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: number]: { title?: string } } = {};

    // Validate file metadata
    filesWithMetadata.forEach((item, index) => {
      if (!item.title.trim()) {
        newErrors[index] = { title: 'Title is required' };
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateUrlRows = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    urlRows.forEach((row, index) => {
      const hasUrl = row.url.trim();
      const hasTitle = row.title.trim();

      // If row has any content, both URL and title are required
      if (hasUrl || hasTitle) {
        if (!hasUrl) {
          errors.push(`Row ${index + 1}: URL is required`);
        } else if (!validateUrl(row.url.trim())) {
          errors.push(`Row ${index + 1}: Invalid URL format`);
        }

        if (!hasTitle) {
          errors.push(`Row ${index + 1}: Title is required`);
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  const handleUpload = async () => {
    // Clear previous validation errors
    setUrlValidationErrors([]);

    // Validate file metadata
    if (!validateForm()) {
      return;
    }

    // Validate URL rows
    const urlValidation = validateUrlRows();
    if (!urlValidation.isValid) {
      // Show toast and set validation errors
      Toastify('error', 'Please fix the URL validation errors below');
      setUrlValidationErrors(urlValidation.errors);
      return;
    }

    // Process URL rows to FileWithMetadata format
    const urlFiles = processUrlRowsToFiles();

    // Combine files and URLs
    const allFilesWithMetadata = [...filesWithMetadata, ...urlFiles];

    if (allFilesWithMetadata.length === 0) {
      Toastify('error', 'Please add at least one file or URL to upload');
      return;
    }

    try {
      setUploading(true);
      console.log('📤 UploadPantryDialog: Starting upload with files and URLs:', allFilesWithMetadata);

      await onUpload(allFilesWithMetadata);

      console.log('✅ UploadPantryDialog: Upload completed successfully');

      // Close dialog immediately after successful upload
      handleClose();

    } catch (error) {
      console.error('❌ UploadPantryDialog: Upload failed:', error);
      // Don't close dialog on error so user can retry
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFilesWithMetadata([]);
      setErrors({});
      setExpandedIndex(null);
      setUrlRows([{ url: '', title: '', description: '', id: 0 }]);
      setNextUrlId(1);
      setUrlValidationErrors([]);
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const fileCount = filesWithMetadata.length;
  const urlCount = urlRows.filter(row => row.url.trim() && row.title.trim()).length;
  const totalCount = fileCount + urlCount;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:max-w-4xl flex flex-col">
        <SheetHeader>
          <SheetTitle>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Upload Pantry Files</h2>
              <p className="text-sm text-gray-600 mt-1">
                Add files to your pantry with individual titles and descriptions
              </p>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-6">
          {/* Drag and Drop Zone */}
          <div className="flex justify-center items-center my-4">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-3 border-dashed rounded-xl transition-all duration-300 relative w-[280px] h-[280px] flex items-center justify-center cursor-pointer ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 hover:scale-[1.02]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
                accept="*/*"
              />
              <div className="flex flex-col items-center gap-4 p-6 text-center">
                {isDragOver ? (
                  <>
                    <CloudUpload size={64} strokeWidth={2} className="text-primary" />
                    <h3 className="text-lg font-semibold">Drop your files here</h3>
                  </>
                ) : (
                  <>
                    <CloudUpload size={56} strokeWidth={1.5} className="text-gray-400" />
                    <div className="text-center">
                      <p className="font-semibold mb-2">Click to select files</p>
                      <p className="text-sm text-gray-500 mb-1">or drag and drop</p>
                      <p className="text-xs text-gray-500">Support for multiple files</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-300" />
            <span className="mx-4 text-sm text-gray-500 font-medium">OR</span>
            <div className="flex-1 border-t border-gray-300" />
          </div>

          {/* URL Upload Section */}
          <div className="px-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Link size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Add from URL</h3>
              </div>
              <Button
                variant="outline"
                onClick={addUrlRow}
                disabled={uploading}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus size={16} />
                Add Row
              </Button>
            </div>

            <div className="flex flex-col gap-4">
              {urlRows.map((row, index) => (
                <div key={row.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {row.url && validateUrl(row.url) && (
                          <FaviconDisplay url={row.url} size={24} />
                        )}
                        <span className="text-sm font-semibold text-gray-900">
                          URL Entry {index + 1}
                        </span>
                      </div>
                      {urlRows.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUrlRow(row.id)}
                          disabled={uploading}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-red-600 hover:bg-red-50"
                        >
                          <X size={18} />
                        </Button>
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="flex gap-3">
                        <div className="flex-[2]">
                          <Label htmlFor={`url-${row.id}`}>URL</Label>
                          <Input
                            id={`url-${row.id}`}
                            placeholder="Enter file URL (e.g., https://example.com/file.pdf)"
                            value={row.url}
                            onChange={(e) => updateUrlRow(row.id, 'url', e.target.value)}
                            disabled={uploading}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex-1">
                          <Label htmlFor={`title-${row.id}`}>Title</Label>
                          <Input
                            id={`title-${row.id}`}
                            placeholder="Enter display title"
                            value={row.title}
                            onChange={(e) => updateUrlRow(row.id, 'title', e.target.value)}
                            disabled={uploading}
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor={`description-${row.id}`}>Description</Label>
                        <textarea
                          id={`description-${row.id}`}
                          placeholder="Enter description (optional)"
                          value={row.description}
                          onChange={(e) => updateUrlRow(row.id, 'description', e.target.value)}
                          disabled={uploading}
                          rows={2}
                          className="w-full mt-1 min-h-[60px] px-3 py-2 border border-input rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* URL Validation Errors */}
            {urlValidationErrors.length > 0 && (
              <div className="mt-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm font-semibold text-red-800 mb-2">
                    Please fix the following URL errors:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {urlValidationErrors.map((error, index) => (
                      <li key={index} className="text-sm text-red-700">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* File List with Individual Metadata */}
          {filesWithMetadata.length > 0 && (
            <div className="flex flex-col px-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Files ({filesWithMetadata.length})
                </h3>
                <p className="text-sm text-gray-600">
                  Click to expand and edit metadata
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {filesWithMetadata.map((item, index) => (
                  <Collapsible
                    key={index}
                    open={expandedIndex === index}
                    onOpenChange={(open) => setExpandedIndex(open ? index : null)}
                    className="border border-gray-200 rounded-lg overflow-hidden mb-2"
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className={`w-full flex items-center p-4 hover:bg-gray-50 transition-colors ${
                        expandedIndex === index ? 'bg-gray-50' : 'bg-white'
                      }`}>
                        <div className="flex items-center flex-1 mr-4 min-w-0">
                          <div className={`w-10 h-10 rounded flex items-center justify-center mr-3 ${
                            item.isUrl ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            {item.isUrl ? (
                              item.url ? (
                                <FaviconDisplay url={item.url} size={24} />
                              ) : (
                                <Link size={18} className="text-blue-700" />
                              )
                            ) : (
                              <Paperclip size={18} className="text-gray-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {item.isUrl ? item.url : item.file?.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-600">
                                {item.isUrl ? 'URL' : formatFileSize(item.file?.size || 0)}
                              </span>
                              {item.title && (
                                <>
                                  <span className="text-xs text-gray-300">•</span>
                                  <span className="text-xs font-medium truncate max-w-[200px]">
                                    {item.title}
                                  </span>
                                </>
                              )}
                              {errors[index]?.title && (
                                <Badge variant="destructive" className="text-xs h-5">
                                  Title required
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            disabled={uploading}
                            className="h-8 w-8 p-0 text-gray-600 hover:text-red-600 hover:bg-red-50 ml-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <ChevronDown
                            size={20}
                            className={`ml-2 text-gray-500 transition-transform ${
                              expandedIndex === index ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pt-4 pb-6 px-4 bg-gray-50 space-y-4">
                        <div>
                          <Label htmlFor={`file-title-${index}`}>Title</Label>
                          <Input
                            id={`file-title-${index}`}
                            value={item.title}
                            onChange={(e) => handleMetadataChange(index, 'title', e.target.value)}
                            disabled={uploading}
                            className={`mt-1 ${errors[index]?.title ? 'border-red-500' : ''}`}
                          />
                          <p className={`text-xs mt-1 ${errors[index]?.title ? 'text-red-600' : 'text-gray-500'}`}>
                            {errors[index]?.title || 'Required: A descriptive title for this file'}
                          </p>
                        </div>
                        <div>
                          <Label htmlFor={`file-description-${index}`}>Description</Label>
                          <textarea
                            id={`file-description-${index}`}
                            value={item.description}
                            onChange={(e) => handleMetadataChange(index, 'description', e.target.value)}
                            disabled={uploading}
                            rows={3}
                            className="w-full mt-1 px-3 py-2 border border-input rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                            placeholder="Optional: Additional details about this file"
                          />
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="border-t pt-4 mt-4">
          <div className="flex-1 flex items-center">
            {totalCount > 0 && (() => {
              const parts = [];
              if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`);
              if (urlCount > 0) parts.push(`${urlCount} URL${urlCount !== 1 ? 's' : ''}`);
              return (
                <p className="text-sm text-gray-600 font-medium">
                  {parts.join(' and ')} ready to upload
                </p>
              );
            })()}
          </div>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleUpload}
            disabled={uploading || (filesWithMetadata.length === 0 && !hasValidUrlEntries())}
            className="flex items-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <CloudUpload size={20} />
                {totalCount > 0 ? `Upload (${totalCount})` : 'Upload'}
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default UploadPantryDialog;
